import { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { ArrowLeft, ChevronRight, BellOff } from "lucide-react";
import { logoutProcessor } from "./helpers/logoutProcessor";
import { useTranslation } from "../i18n";
import FlowLoader from "./FlowLoader";
import HiddenScanInput from "./HiddenScanInput";
import { apiFetch } from "../api/apiFetch";
import "../styles/hidden-scan-input.css";

const formatElapsed = (seconds: number): string => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    if (h > 0) return `${h}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    return `${m}:${String(s).padStart(2, "0")}`;
};

const ChuteTimer = ({ timeStamp, onUrgentChange }: { timeStamp: string; onUrgentChange?: (urgent: boolean) => void }) => {
    const [elapsed, setElapsed] = useState(0);

    useEffect(() => {
        const created = new Date(timeStamp).getTime();
        const update = () => {
            const secs = Math.max(0, Math.floor((Date.now() - created) / 1000));
            setElapsed(secs);
            onUrgentChange?.(secs >= 60);
        };
        update();
        const interval = setInterval(update, 1000);
        return () => clearInterval(interval);
    }, [timeStamp, onUrgentChange]);

    return <span className="conveyor-list-item-timer">{formatElapsed(elapsed)}</span>;
};

const getTimeClass = (timeStamp: string): string => {
    const elapsed = Math.max(0, Math.floor((Date.now() - new Date(timeStamp).getTime()) / 1000));
    if (elapsed >= 60) return "conveyor-list-item-red";
    if (elapsed >= 30) return "conveyor-list-item-amber";
    return "";
};

const ChuteCard = ({ chute, alerting, onClick }: { chute: CompleteChute; alerting: boolean; onClick: () => void }) => {
    const [timeClass, setTimeClass] = useState(() => getTimeClass(chute.timeStamp));

    useEffect(() => {
        const interval = setInterval(() => setTimeClass(getTimeClass(chute.timeStamp)), 1000);
        return () => clearInterval(interval);
    }, [chute.timeStamp]);

    const isColoured = timeClass !== "";

    return (
        <div
            className={`conveyor-list-item ${timeClass} ${alerting ? "conveyor-list-item-pulse" : ""}`}
            onClick={onClick}
        >
            <div className="conveyor-list-item-info">
                <h3 className="conveyor-list-item-chute">{chute.chute}</h3>
                <p>{chute.palletId}</p>
            </div>
            <div className="conveyor-list-item-right">
                <ChuteTimer timeStamp={chute.timeStamp} onUrgentChange={() => { }} />
                <ChevronRight size={20} color={isColoured ? "rgba(255,255,255,0.7)" : "var(--color-text-muted)"} />
            </div>
        </div>
    );
};

type CompleteChute = {
    axiomId: number;
    chute: string;
    palletId: string;
    system: string;
    timeStamp: string;
    tag: string;
    type: string;
    receivedItems: boolean;
    collected: boolean;
    processed: string;
};

type ConveyorCompleteChutesProps = {
    onSelectChute: (chute: CompleteChute, chuteScanned: boolean) => void;
    goBack: () => void;
};

// Notification sound — pre-generate buffer at module load (no user gesture needed)
let notifBuffer: AudioBuffer | null = null;
let notifCtx: AudioContext | null = null;

const getNotifCtx = (): AudioContext => {
    if (!notifCtx) notifCtx = new (window.AudioContext || (window as any).webkitAudioContext)();
    return notifCtx;
};


const generateNotifBuffer = async (ctx: AudioContext): Promise<AudioBuffer> => {
    const duration = 0.4;
    const sampleRate = ctx.sampleRate;
    const offlineCtx = new OfflineAudioContext(1, Math.ceil(sampleRate * duration), sampleRate);

    const osc1 = offlineCtx.createOscillator();
    const gain1 = offlineCtx.createGain();
    osc1.type = "sine"; osc1.frequency.value = 784;
    gain1.gain.setValueAtTime(0.4, 0);
    gain1.gain.exponentialRampToValueAtTime(0.001, 0.15);
    osc1.connect(gain1); gain1.connect(offlineCtx.destination);
    osc1.start(0); osc1.stop(0.15);

    const osc2 = offlineCtx.createOscillator();
    const gain2 = offlineCtx.createGain();
    osc2.type = "sine"; osc2.frequency.value = 1047;
    gain2.gain.setValueAtTime(0.45, 0.15);
    gain2.gain.exponentialRampToValueAtTime(0.001, 0.4);
    osc2.connect(gain2); gain2.connect(offlineCtx.destination);
    osc2.start(0.15); osc2.stop(0.4);

    return offlineCtx.startRendering();
};

let lastNotifTime = 0;
const playNotifSound = () => {
    const now = Date.now();
    if (now - lastNotifTime < 1000) return; // Throttle: max once per second
    lastNotifTime = now;
    try {
        const ctx = getNotifCtx();
        if (ctx.state === "suspended") ctx.resume();
        if (notifBuffer) {
            const source = ctx.createBufferSource();
            source.buffer = notifBuffer;
            source.connect(ctx.destination);
            source.start();
        }
    } catch { }
};

const vibrateDevice = () => {
    try { navigator.vibrate?.([200, 100, 200]); } catch { }
};

// Pre-generate notification buffer at module load (no user gesture needed for OfflineAudioContext)
if (typeof window !== "undefined") {
    (async () => {
        try {
            const ctx = getNotifCtx();
            if (!notifBuffer) notifBuffer = await generateNotifBuffer(ctx);
        } catch { }
    })();
}

const NOTIF_INITIAL_MS = 12000;
const NOTIF_DECAY = 0.9; // 10% reduction each time
const NOTIF_FLOOR_MS = 5000;

const ConveyorCompleteChutes = ({ onSelectChute, goBack }: ConveyorCompleteChutesProps) => {
    const nav = useNavigate();
    const { t } = useTranslation();
    const [chutes, setChutes] = useState<CompleteChute[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [audioAllowed, setAudioAllowed] = useState<boolean>(false);
    const [alerting, setAlerting] = useState<boolean>(false);
    const [scan, setScan] = useState<string>("");

    const dismissedRef = useRef(false);
    const notifTimerRef = useRef<NodeJS.Timeout | null>(null);
    const notifDelayRef = useRef(NOTIF_INITIAL_MS);
    const hasChutesRef = useRef(false);

    const userData = window.localStorage.getItem("user");
    const userObject = userData !== null ? JSON.parse(userData) : { warehouse: "Newport" };

    // Init audio
    const initAudio = useCallback(async () => {
        try {
            const ctx = getNotifCtx();
            if (ctx.state === "suspended") await ctx.resume();
            if (!notifBuffer) notifBuffer = await generateNotifBuffer(ctx);
            setAudioAllowed(true);
            return true;
        } catch {
            setAudioAllowed(false);
            return false;
        }
    }, []);

    const handleGrantPermission = useCallback(async () => {
        const ok = await initAudio();
        if (ok) playNotifSound();
    }, [initAudio]);

    useEffect(() => { initAudio(); }, [initAudio]);

    // Escalating notification loop — schedules itself with decreasing delay
    const scheduleNextNotif = useCallback(() => {
        if (notifTimerRef.current) clearTimeout(notifTimerRef.current);

        notifTimerRef.current = setTimeout(() => {
            if (dismissedRef.current || !hasChutesRef.current) return;

            playNotifSound();
            vibrateDevice();

            // Reduce delay by 10%, floor at 5s
            notifDelayRef.current = Math.max(notifDelayRef.current * NOTIF_DECAY, NOTIF_FLOOR_MS);

            // Schedule next
            scheduleNextNotif();
        }, notifDelayRef.current);
    }, []);

    const startNotifs = useCallback(() => {
        if (dismissedRef.current) return;
        setAlerting(true);

        // Play immediately on first trigger
        playNotifSound();
        vibrateDevice();

        // Reset delay and start escalation
        notifDelayRef.current = NOTIF_INITIAL_MS;
        scheduleNextNotif();
    }, [scheduleNextNotif]);

    const stopNotifs = useCallback(() => {
        if (notifTimerRef.current) {
            clearTimeout(notifTimerRef.current);
            notifTimerRef.current = null;
        }
        setAlerting(false);
        notifDelayRef.current = NOTIF_INITIAL_MS;
    }, []);

    // Cleanup on unmount
    useEffect(() => () => {
        if (notifTimerRef.current) clearTimeout(notifTimerRef.current);
    }, []);

    // Fetch chutes
    const fetchChutes = useCallback(async () => {
        await apiFetch(`/conveyor/chutes/complete/uncollected`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ warehouse: userObject.warehouse || "Newport" })
        }).then(async (res) => {
            const response = await res.json();
            if (res.ok) {
                setChutes(response);
                hasChutesRef.current = response.length > 0;

                if (response.length > 0 && !dismissedRef.current && audioAllowed && !notifTimerRef.current) {
                    startNotifs();
                }

                if (response.length === 0) {
                    stopNotifs();
                }
            } else if (res.status === 401) {
                logoutProcessor();
                nav("/login");
            } else {
                toast.error(t("fetch_chutes_failed"));
            }
        }).catch(() => {
            toast.error(t("fetch_chutes_failed"));
        });
        setLoading(false);
    }, [audioAllowed, nav, t, startNotifs, stopNotifs]);

    // Poll every 3s for fast detection of new chutes
    useEffect(() => {
        fetchChutes();
        const interval = setInterval(fetchChutes, 1000);
        return () => clearInterval(interval);
    }, [fetchChutes]);

    const handleChuteClick = useCallback((chute: CompleteChute) => {
        dismissedRef.current = true;
        stopNotifs();
        onSelectChute(chute, false);
    }, [onSelectChute, stopNotifs]);

    const handleScan = useCallback((scannedValue?: string) => {
        const val = (scannedValue ?? scan).trim();
        if (val === "") return;

        const correctedScan = Number(val.slice(2)).toString();
        const match = chutes.find(c => c.chute === correctedScan);

        setScan("");

        if (!match) {
            toast.dismiss(); toast.error(t("wrong_chute"));
            return;
        }

        dismissedRef.current = true;
        stopNotifs();
        onSelectChute(match, true);
    }, [scan, chutes, onSelectChute, stopNotifs, t]);

    const handleBack = useCallback(() => {
        dismissedRef.current = true;
        stopNotifs();
        goBack();
    }, [goBack, stopNotifs]);

    return (
        <div className="page-data page-data-large" style={{ minHeight: "calc(100vh - 56px)", background: "white" }}>
            <div className="page-title-bar">
                <h1 className="page-title">{t("complete_chutes")}</h1>
                <button className="page-back-btn" onClick={handleBack}><ArrowLeft size={20} /></button>
            </div>

            {loading && <FlowLoader />}

            {!loading && chutes.length === 0 && (
                <p className="conveyor-list-empty">{t("no_chutes")}</p>
            )}

            {!loading && chutes.length > 0 && (
                <div className="conveyor-list">
                    {chutes.map((chute, index) => (
                        <ChuteCard
                            key={chute.axiomId}
                            chute={chute}
                            alerting={alerting}
                            onClick={() => handleChuteClick(chute)}
                        />
                    ))}
                </div>
            )}

            {!audioAllowed && (
                <div className="scan-input-toggles">
                    <button
                        type="button"
                        className="scan-input-toggle-btn chute-notif-btn"
                        onClick={handleGrantPermission}
                        aria-label="Enable notifications"
                    >
                        <BellOff size={20} />
                    </button>
                </div>
            )}

            <HiddenScanInput
                value={scan}
                onChange={setScan}
                onSubmit={handleScan}
            />
        </div>
    );
};

export default ConveyorCompleteChutes;
